<template>
  <div>
    <div v-if="board">
      <page-header>
        <div class="categories ml-3 flex-1 text-nowrap d-flex">
          <div class="boardTitle mr-4 ml-2 font-weight-bold align-self-center">
            {{ board.title }}
          </div>

          <!-- Add Task -->
          <template-selector
            v-if="board"
            class="align-self-center"
            :board-guid="board.id"
            :disabled="addingTaskFromTemplate"
            @add-blank="addTask"
            @select="addTaskFromTemplate"
          />

          <!-- Filters -->
          <div class="d-flex flex-column">
            <!-- Board items Filter -->
            <div>
              <div
                class="badgebutton ml-4"
                :class="selectedCategories.length === 0 && !displayIsRelevantTasksOnly ? 'active' : ''"
                @click="chooseCategory('all')"
              >
                All
              </div>
              <div
                class="badgebutton ml-2"
                :class="selectedCategories.indexOf(category.id) !== -1 ? 'active' : ''"
                v-for="category in boardStore.availableCategories"
                :key="category.id"
                @click="chooseCategory(category.id)"
              >
                {{ category.name }}
              </div>
            </div>

            <!-- Special Occurrence filter -->
            <div class="mt-2 justify-content-start" v-if="availableSpecialOccurrences && availableSpecialOccurrences.length">
              <div
                class="badgebutton ml-4 color-white"
                :class="selectedOcurrences.length === 0 ? 'secondary' : ''"
                @click="chooseOccurrence()"
              >
                <i class="far fa-filter"></i>
                No filter
              </div>
              <div
                class="badgebutton ml-2 color-white"
                :class="selectedOcurrences.length === availableSpecialOccurrences.length ? 'secondary' : ''"
                @click="chooseOccurrence(-1)"
              >
                <i class="far fa-filter"></i>
                Select all
              </div>
              <div class="badgebutton ml-2 color-white" :class="selectNone ? 'secondary' : ''" @click="toggleSelectNone()">
                <i class="far fa-filter"></i>
                Select none
              </div>
              <div
                class="badgebutton ml-2 color-white"
                :class="selectedOcurrences.indexOf(occ.id) !== -1 ? 'secondary' : ''"
                v-for="occ in availableSpecialOccurrences"
                :key="occ.id"
                @click="chooseOccurrence(occ.id)"
              >
                {{ occ.name }}
              </div>
            </div>
          </div>

          <div class="d-flex flex-column">
            <div>
              <div class="ml-4 mr-2 align-self-center">
                <div class="d-flex justify-content-left">
                  <div v-for="label in selectedLabels" :key="label.id">
                    <div
                      class="mr-2 d-flex justify-content-between align-items-center"
                      :style="{
                        'background-color': label.color,
                        color: 'white',
                        padding: '2px 5px',
                        'border-radius': '3px',
                        'font-size': '1rem',
                      }"
                    >
                      <i v-if="label.icon" :class="label.icon" class="mr-1 ml-1"></i>
                      {{ label.title }}
                      <div>
                        <i class="ml-2 fas fa-times cursor-pointer" @click="removeLabel(label)"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div v-if="selectedUser">
              <div class="ml-4 mt-2 mr-2 align-self-center">
                <div class="d-flex justify-content-left cursor-pointer" v-on:click="selectedUser = null">
                  <div class="avatar avatar-xs d-flex align-items-center">
                    <span>{{ selectedUser.shortName.substr(0, 3) }}</span>
                    <div>
                      <i style="color: #333" class="ml-2 fas fa-times"></i>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <template v-slot:right>
          <div class="mr-4 d-flex align-items-center">
            <div
              class="badgebutton ml-2"
              :class="displayIsRelevantTasksOnly ? 'active' : '' ? 'active' : ''"
              @click="processIsRelevantFilter()"
            >
              Report Relevant
            </div>
          </div>
          <div class="mr-4 d-flex align-items-center">
            <!-- Search -->
            <div class="input-group input-group-sm ml-2 mr-3 text-nowrap">
              <div class="input-group-prepend">
                <span class="input-group-text">
                  <i class="far fa-search"></i>
                </span>
              </div>
              <input type="text" v-model="searchString" class="form-control" placeholder="Search..." aria-label="Username" />
            </div>

            <div class="currentShift" v-if="boardStore.board.enableShifts">
              <div>Current Shift</div>
              <div v-if="boardStore.board.currentShift">
                {{ currentShiftFrom | formatDateTime }}
                <br />
                {{ currentShiftTo | formatDateTime }}
              </div>
              <div v-else>NO SHIFT</div>
            </div>

            <div v-if="boardStore.board.enableShifts">
              <div v-if="showShiftPreparation" class="badgebutton success text-nowrap" @click="confirmShiftPreparation">
                Finalize Shift Handover
              </div>
              <div v-else class="badgebutton success active text-nowrap" @click="showModal('shiftHandover')">Handover to next Shift</div>
            </div>

            <router-link
              tag="div"
              :to="{
                name: 'shifts-overview',
                params: { id: boardStore.currentBoardId },
              }"
              v-if="boardStore.board.enableShifts"
              class="badgebutton active text-nowrap c-white ml-2"
            >
              <i class="far fa-clock"></i>
              Shifts Overview
            </router-link>

            <router-link
              tag="div"
              :to="{
                name: 'board-overview',
                params: { id: boardStore.currentBoardId },
              }"
              class="badgebutton active text-nowrap c-white ml-2"
            >
              <i class="far fa-clock"></i>
              Board Overview
            </router-link>

            <div class="badgebutton danger active text-nowrap ml-2" v-if="userStore.user.isAdmin" @click="confirmCleanup">
              <i class="far fa-broom"></i>
              Cleanup board
            </div>

            <div class="badgebutton danger active text-nowrap ml-2" v-if="canArchiveDone" @click="confirmArchiveDone">
              <i class="far fa-broom"></i>
              Archive done
            </div>

            <router-link
              v-if="currentUserIsBoardAdmin"
              tag="div"
              :to="{
                name: routes.settings,
                params: { id: boardStore.currentBoardId },
              }"
              class="badgebutton info active text-nowrap ml-2"
            >
              <i class="far fa-cogs"></i>
            </router-link>

            <router-link
              tag="div"
              :to="{
                name: routes.labels.statistic.name,
                params: { id: boardStore.currentBoardId },
              }"
              class="badgebutton info active text-nowrap ml-2 mr-2"
            >
              <i class="fa fa-tags"></i>
            </router-link>

            <div v-if="board && board.title && board.title.toLowerCase() === 'moc'">
              <div class="badgebutton info active text-nowrap ml-2" id="po">
                <i class="far fa-info-circle"></i>
                Due Dates
              </div>
              <b-popover target="po" triggers="hover" placement="bottom">
                <template #title>Due Date calculator</template>
                <moc-due-date-calculator></moc-due-date-calculator>
              </b-popover>
            </div>

            <div class="badgebutton" @click="demo(0)" v-if="displayDemoButtons">[1]</div>
            <div class="badgebutton" @click="demo(1)" v-if="displayDemoButtons">[2]</div>
          </div>
        </template>
      </page-header>

      <div class="d-flex">
        <div
          class="bahn"
          v-for="list in orderedLists"
          :key="list.id"
          :class="[
            list.listGroupType === ListGroupType.Delegated || list.listGroupType === ListGroupType.Shared ? 'ml-auto' : '',
            list.listGroupType === ListGroupType.Information ? 'information' : '',
          ]"
        >
          <div class="headers">
            <span class="pl-2 all-info" @click="showAllInfo = !showAllInfo">
              <span v-if="showAllInfoBtn(list)">
                <i v-b-tooltip.hover title="Hide old information items" v-if="showAllInfo" class="far fa-eye-slash"></i>
                <i v-b-tooltip.hover title="Show all information items" v-else class="far fa-eye"></i>
              </span>
            </span>

            {{ list.name }}
            <span v-on:click="toggleList(list.id)" class="arrow pr-2">
              <i v-if="isListToggled(list.id)" class="far fa-plus"></i>
              <i v-else class="far fa-minus"></i>
            </span>
          </div>

          <div class="containers">
            <div v-if="list.listGroupType === ListGroupType.Information" class="dnd-disabled">
              <s-o-card v-for="so in boardOccurrences" :item="so" :key="so.id"></s-o-card>
            </div>

            <Container
              :group-name="list.listGroup"
              :key="list.id"
              @drop="(e) => onDrop(list, e)"
              @drag-start="(e) => onDrag(list.id, e)"
              non-drag-area-selector=".dnd-disabled"
              :get-child-payload="getChildPayload(list.id)"
              drag-class="card-ghost"
              drop-class="card-ghost-drop"
            >
              <Draggable v-for="item in filteredBoardItems(list)" :key="item.guid">
                <!-- Delegated items -->
                <div class="dnd-disabled" v-if="item.key && item.isGrouped && item.groupedItems.length && !isListToggled(list.id)">
                  <div class="board-card d-flex justify-content-between">
                    <div class="pl-2">
                      {{ item.key.title }}
                    </div>

                    <span v-on:click="toggleList(item.key.id)" class="arrow pr-2">
                      <i v-if="isListToggled(item.key.id)" class="far fa-plus"></i>
                      <i v-else class="far fa-minus"></i>
                    </span>
                  </div>
                  <div v-if="!isListToggled(item.key.id)">
                    <div v-for="delegatedItem in item.groupedItems" :key="delegatedItem.key">
                      <task-card
                        :item="delegatedItem"
                        :list="list"
                        v-if="isValidItemDate(delegatedItem, list)"
                        class="dnd-disabled m-1"
                        :class="{
                          'd-none': isItemHidden(delegatedItem),
                        }"
                      ></task-card>
                    </div>
                  </div>
                </div>

                <!-- Normal items -->
                <div v-if="!item.isGrouped">
                  <div
                    class="timecard dnd-disabled"
                    v-if="item.isFix && list.displayShiftTime && !isListToggled(list.id)"
                    :class="item.isNextShift ? 'nextshift' : ''"
                    @click="toggleItems(item)"
                  >
                    <div class="d-flex justify-content-between align-center">
                      <div>
                        <i class="far fa-clock"></i>
                        {{ item.title }}
                      </div>
                      <div v-if="countSeparatorItems(item) > 0 && toggledSeparators.indexOf(item.sortOrder) !== -1">
                        <span class="badge badge-warning">
                          {{ countSeparatorItems(item) }}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div v-else>
                    <task-card
                      :item="item"
                      :list="list"
                      v-if="!isListToggled(list.id) && isValidItemDate(item, list)"
                      :class="{
                        'd-none': isItemHidden(item),
                        'dnd-disabled':
                          (item.isDelegated && list.listGroupType === ListGroupType.Delegated) ||
                          list.listGroupType === ListGroupType.Shared,
                      }"
                      class="m-1"
                    ></task-card>
                  </div>
                </div>
              </Draggable>

              <transition name="fade">
                <div class="c-container" v-if="isListToggled(list.id)">Collapsed</div>
              </transition>
            </Container>
          </div>
        </div>
      </div>

      <router-view></router-view>

      <AppInfo />

      <b-modal ref="shiftHandover" id="shiftHandover" hide-header hide-footer size="wide" title="Shift Handover" body-class="modal-body-wo">
        <shift-handover></shift-handover>
      </b-modal>
    </div>

    <div v-else>
      <page-header>
        <div class="badgebutton active text-nowrap c-white ml-4">
          <router-link class="mt-3 mb-2" :to="{ name: 'Boards' }">Back to Board list</router-link>
        </div>
      </page-header>

      <div class="d-flex align-items-center justify-content-center mt-4">
        <div class="no-access-error">This board does not exist or you are not authorized for this board</div>
      </div>
    </div>

    <b-modal id="delegate-to" ref="delegate-to" hide-header hide-footer title="Delegate to" body-class="modal-body-wo" size="m">
      <modal-window :main-width="12">
        <div class="delegate-modal-content">
          <h1 class="mb-4">Delegate to</h1>

          <div v-for="board in allboardsExceptCurrent" :key="board.id">
            <button class="btn btn-block btn-sm btn-success mt-2 mb-2" @click="delegate(board)">
              <i class="fas fa-share icon-left"></i>
              {{ board.title }}
            </button>
          </div>

          <b-button
            class="btn btn-block btn-sm mt-4"
            variant="outline-danger"
            block
            @click="
              itemIdToDelegate = '';
              $refs['delegate-to'].hide();
            "
          >
            Cancel
          </b-button>
        </div>
      </modal-window>
    </b-modal>

    <b-modal
      id="proccess-future-item"
      ref="proccess-future-item"
      hide-header
      hide-footer
      title="Specify Planned date"
      body-class="modal-body-wo"
      size="m"
    >
      <modal-window :main-width="12">
        <div class="proccess-future-item">
          <h1 class="mb-4">Select Planned Date</h1>

          <board-item-date-picker v-model="boardItemPlannedDate"></board-item-date-picker>

          <div class="d-flex justify-content-between">
            <b-button
              :disabled="!boardItemPlannedDate"
              class="btn btn-block btn-sm mt-4 mr-2"
              variant="success"
              block
              @click="endDropFutureBoardItem()"
            >
              Save
            </b-button>
            <b-button class="btn btn-block btn-sm mt-4 ml-2" variant="danger" block @click="cancelDropFutureBoardItem()">Cancel</b-button>
          </div>
        </div>
      </modal-window>
    </b-modal>

    <b-modal id="reject-delegate" ref="reject-delegate" hide-header hide-footer body-class="modal-body-wo" size="m">
      <modal-window :main-width="12">
        <div class="delegate-modal-content">
          <h1 class="mb-4">Delegate to</h1>

          <span>
            You are not allowed to delegate already delegated items. If you want to give this item back to the delegator, please open the
            item and click on 'Reject / Give back'
          </span>

          <b-button class="btn btn-block btn-sm mt-4" variant="outline-success" block @click="$refs['reject-delegate'].hide()">Ok</b-button>
        </div>
      </modal-window>
    </b-modal>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import { Container, Draggable } from 'vue-smooth-dnd';
  import TaskCard from '@/components/TaskCard.vue';
  import TaskDetail from '@/components/TaskDetail/TaskDetail.vue';
  import TemplateSelector from '@/components/Board/TemplateSelector.vue';
  import Component from 'vue-class-component';
  import {
    BoardItemDto,
    BoardItemListDto,
    BoardItemStatusDto,
    IBoardDto,
    IBoardItemDto,
    IBoardItemListDto,
    LabelDto,
    ListGroupTypeDto
  } from '@/apiclient/apiclient_generated';
  import { BoardModule, BoardStore } from '@/store/board';
  import { LabelModule, LabelStore } from '@/store/label';
  import { NotificationModule, NotificationStore } from '@/store/notification';
  import PageHeader from '@/components/PageHeader.vue';
  import ShiftHandover from '@/components/ShiftHandover.vue';
  import { DateTime } from 'luxon';
  import BISeparator from '@/types/boardItemSeparator';
  import RouteNames from '@/types/routeNames';
  import { ShiftModule, ShiftStore } from '@/store/shift';
  import { SettingsModule, SettingsStore } from '@/store/settings';
  import { ConfirmModal } from '@/helpers/confirm-modal';
  import ModalWindow from '@/components/ModalWindow.vue';
  import { SpecialOccurrenceModule, SpecialOccurrenceStore } from '@/store/specialOccurrence';
  import UserTimeZone from '@/components/User/UserTimeZone.vue';
  import MocDueDateCalculator from '@/components/Misc/MocDueDateCalculator.vue';
  import SOCard from '@/components/SOCard.vue';
  import { UserModule, UserStore } from '@/store/user';
  import { ENABLE_DEMO_FEATURES } from '@/config';

  @Component({
    components: {
      SOCard,
      MocDueDateCalculator,
      UserTimeZone,
      PageHeader,
      Container,
      Draggable,
      TaskCard,
      TaskDetail,
      ShiftHandover,
      ModalWindow,
      TemplateSelector,
    },
  })
  export default class BoardComponent extends Vue {
    private boardStore: BoardModule = BoardStore;
    private labelStore: LabelModule = LabelStore;
    private userStore: UserModule = UserStore;
    private occurrenceStore: SpecialOccurrenceModule = SpecialOccurrenceStore;
    private settingsStore: SettingsModule = SettingsStore;
    private shiftStore: ShiftModule = ShiftStore;
    private notificationStore: NotificationModule = NotificationStore;
    private itemIdToDelegate = '';

    displayIsRelevantTasksOnly = false;
    private selectedCategories: string[] = [];
    private selectedLabels: LabelDto[] = [];
    private selectedOcurrences = [];
    private selectedUser = null;

    ListGroupType = ListGroupTypeDto;
    searchString = '';
    toggledSeparators: any[] = [];
    toggledLists: any[] = [];
    boardItemStatus = BoardItemStatusDto;
    showAllInfo = false;
    routes = RouteNames;

    private dndParams: any = {};
    boardItemPlannedDate = null;

    get displayDemoButtons() {
      return ENABLE_DEMO_FEATURES;
    }

    get canArchiveDone(): boolean {
      //only available on boards which dont use shifts
      if (this.boardStore.board.enableShifts) return false;

      if (this.userStore.user.isAdmin || this.userStore.user.isSuperAdmin) return true;

      if (
        this.userStore.user &&
        this.userStore.user.boardPermissions &&
        this.userStore.user.boardPermissions.find((_) => _.boardGuid == this.boardStore.board.id)?.isBoardAdmin
      )
        return true;

      return false;
    }

    toggleList(listId) {
      const index = this.toggledLists.indexOf(listId);
      if (index >= 0) {
        this.toggledLists.splice(index, 1);
      } else {
        this.toggledLists.push(listId);
      }
    }

    get availableSpecialOccurrences() {
      const soIds = this.lodash.uniq(this.boardStore.boardItems.map((_) => _.specialOccurrenceId)).filter((_) => !!_);

      if (!soIds || !soIds.length) {
        return [];
      }

      const result = [];

      const occurrences = [
        ...this.occurrenceStore.activeAndArchivedOccurrences.activeSpecialOccurrences,
        ...this.occurrenceStore.activeAndArchivedOccurrences.archivedSpecialOccurrences,
      ];

      soIds.forEach((_) => {
        const so = occurrences.find((occ) => occ.id === _);

        if (so) {
          result.push({
            id: so.id,
            name: so.name,
          });
        }
      });

      return result;
    }

    get boardOccurrences() {
      const occurrences = this.occurrenceStore.groupedOccurrences.filter((_) => _.boardIds.indexOf(this.board.id) !== -1);

      return occurrences;
    }

    get allboardsExceptCurrent() {
      return this.lodash.orderBy(this.boardStore.allBoardsExceptCurrent, 'title');
    }

    get showShiftPreparation() {
      return (
        this.boardStore.board.isValidationEnabled &&
        this.boardStore.board.currentShift &&
        !this.boardStore.board.currentShift.isHandoverPrepared
      );
    }

    get board(): IBoardDto {
      return this.boardStore.board;
    }

    private showAllInfoBtn(list) {
      return !this.isListToggled(list.id) && list.newBoardItemStatus === this.boardItemStatus.Information;
    }

    get currentUserIsBoardAdmin() {
      if (!this.userStore.user || !this.userStore.user.boardPermissions) return false;

      return (
        this.userStore.user.isAdmin ||
        this.userStore.user.boardPermissions.filter((_) => _.boardGuid == this.board.id && _.isBoardAdmin).length > 0
      );
    }

    isValidItemDate(item: IBoardItemDto) {
      if (item.boardItemStatus === this.boardItemStatus.Information && item.validToDate && !this.showAllInfo) {
        const now = DateTime.utc();
        const fromDate = DateTime.fromISO(item.validFromDate);
        const durationFrom = fromDate.diff(now, 'minutes').toObject().minutes;

        const toDate = DateTime.fromISO(item.validToDate);
        const durationTo = toDate.diff(now, 'minutes').toObject().minutes;

        return durationFrom <= 0 && durationTo >= 0;
      }
      return true;
    }

    isListToggled(listId) {
      return this.toggledLists.includes(listId);
    }

    async mounted() {
      await Promise.all([
        this.boardStore.loadAllBoards(),

        this.labelStore.getAllLabelsAsync(this.$route.params.id),
        this.labelStore.loadSelectedBoardLabels(this.$route.params.id),

        this.boardStore.loadData(this.$route.params.id),
        this.occurrenceStore.getActiveAndArchivedOccurrences(),
        this.occurrenceStore.loadTechnicalOccurrenceTypes(),
        this.occurrenceStore.getAll(),

        this.occurrenceStore.getAllGroupedAsync(),

        this.occurrenceStore.getAircrafts(),
      ]);

      if (!this.boardStore.board?.id) {
        return;
      }

      await this.$opstaskhub.subscribeToBoard(this.boardStore.currentBoardId);
      await this.notificationStore.loadAllNotifications(this.$route.params.id);

      // recalculate times etc.
      setInterval(() => this.$forceUpdate(), 30000);
    }

    async created() {
      this.$root.$on('boardItem::labelSelected', (label: LabelDto) => {
        if (!this.selectedLabels.some((_) => _.id === label.id)) {
          this.selectedLabels.push(label);
        }
      });

      this.$root.$on('boardItem::userSelected', (user) => {
        this.selectedUser = user;
      });
    }

    async beforeDestroy() {
      if (this.boardStore.board?.id) {
        await this.$opstaskhub.leaveBoard(this.boardStore.currentBoardId);
      }

      this.occurrenceStore.setGroupedOccurrences([]);
    }

    destroyed() {
      //prevent flickering of boardItems of "previous" board
      this.boardStore.cleanup();
    }

    get currentShiftFrom() {
      return this.boardStore.board.currentShift.dateFrom;
    }

    get currentShiftTo() {
      return this.boardStore.board.currentShift.dateTo;
    }

    async delegate(newBoard) {
      const currentBoardId = this.boardStore.currentBoardId;
      const newBoardId = newBoard.id;
      const boardItemId = this.itemIdToDelegate;

      const success = () => {
        this.boardStore.delegate({ currentBoardId, newBoardId, boardItemId });
        this.$root.$emit('bv::hide::modal', 'delegate-to');
      };

      ConfirmModal.confirm(
        this,
        `Are you sure you want to delegate this item to ${newBoard.title} ?`,
        'Confirm delegate to',
        'Yes, delegate',
        'No',
        success,
        null
      );
    }

    async onDrop(collection: BoardItemListDto, dropResult) {
      if (this.isConfidentialMove(dropResult.payload, collection)) {
        //dropping Confidential items is not supported
        return;
      }

      if (collection.listGroupType === ListGroupTypeDto.Shared) {
        //dropping on shared not supported
        return;
      }

      if (collection.listGroupType === ListGroupTypeDto.Delegated && dropResult.addedIndex !== null) {
        const boardItem: IBoardItemDto = dropResult.payload;
        const currentBoard = this.boardStore.currentBoardId;

        if (boardItem.isDelegated && boardItem.ownerBoardId !== currentBoard) {
          this.showModal('reject-delegate');
          return;
        }

        const newIndex = dropResult.addedIndex;

        if (newIndex === 0) {
          this.itemIdToDelegate = dropResult.payload.id;
          this.showModal('delegate-to');
        } else {
          const groupedList = this.filteredBoardItems(collection);
          const boardData = groupedList[newIndex - 1];

          if (!boardData) {
            return;
          }

          const board: any = boardData.key;

          const success = () => {
            this.boardStore.delegate({
              currentBoard,
              newBoardId: board.id,
              boardItemId: dropResult.payload.id,
            });
          };

          ConfirmModal.confirm(
            this,
            `Are you sure you want to delegate this item to ${board.title} ?`,
            'Confirm delegate to',
            'Yes, delegate',
            'No',
            success,
            null
          );
        }
      } else {
        const moveParams = {
          collection: collection.id,
          removedIndex: dropResult.removedIndex,
          addedIndex: dropResult.addedIndex,
          payload: dropResult.payload,
          isFutureItem: false,
        };

        //check if we find a future item above to display modal
        if (collection.displayShiftTime) {
          if (moveParams.addedIndex !== moveParams.removedIndex) {
            const items = this.boardStore.items.filter((_) => _.boardItemListId === collection.id);

            for (let index = 0; index < moveParams.addedIndex; index++) {
              const upperItem: any = items[moveParams.addedIndex - index];
              if (upperItem && upperItem.isFix && upperItem.isFuture && moveParams.addedIndex >= moveParams.removedIndex) {
                moveParams.isFutureItem = true;
                this.boardItemPlannedDate = upperItem.value.toLocal().toFormat("yyyy-MM-dd'T'HH:mm:ss");
                break;
              }
            }
          }
        }

        if (moveParams.isFutureItem) {
          this.dndParams = moveParams;
          this.showModal('proccess-future-item');
        } else {
          await this.boardStore.endDrag(moveParams);
        }
      }
    }

    async endDropFutureBoardItem() {
      this.dndParams.reorder = true;
      this.dndParams.payload.plannedDate = DateTime.fromISO(this.boardItemPlannedDate).toUTC().toFormat("yyyy-MM-dd'T'HH:mm'Z'");
      await this.boardStore.endDrag(this.dndParams);
      this.cancelDropFutureBoardItem();
    }

    cancelDropFutureBoardItem() {
      this.dndParams = {};
      this.boardItemPlannedDate = '';
      this.$root.$emit('bv::hide::modal', 'proccess-future-item');
    }

    get orderedLists(): IBoardItemListDto[] {
      return this.lodash.orderBy(this.boardStore.board.lists, 'sortOrder');
    }

    onDrag(collection, dragResult) {
      this.boardStore.startDrag(dragResult.payload.id);
    }

    getChildPayload(list) {
      return (index) => {
        const data = this.filteredItems.filter((x) => x.boardItemListId === list)[index];
        return data;
      };
    }

    toggleItems(item: any) {
      if (this.toggledSeparators.indexOf(item.sortOrder) != -1) {
        this.toggledSeparators = this.toggledSeparators.filter((_) => _ != item.sortOrder);
      } else {
        this.toggledSeparators.push(item.sortOrder);
      }
    }

    isItemHidden(item: IBoardItemDto) {
      const sortOrder = Math.trunc(item.sortOrder).toString().substr(0, 10) + '00';

      if (this.toggledSeparators.filter((_) => _ == sortOrder).length) return true;
      else return false;
    }

    countSeparatorItems(separator) {
      return this.filteredItems.filter((_) => Math.trunc(_.sortOrder).toString().substr(0, 10) + '00' == separator.sortOrder).length - 1; //-1 because we count the separators;
    }

    processIsRelevantFilter() {
      this.displayIsRelevantTasksOnly = !this.displayIsRelevantTasksOnly;
      this.selectedCategories = [];

      if (!this.displayIsRelevantTasksOnly) {
        this.chooseCategory('all');
      }
    }

    chooseCategory(category) {
      if (category === 'all') {
        this.selectedCategories = [];
        this.displayIsRelevantTasksOnly = false;
      } else {
        this.displayIsRelevantTasksOnly = false;

        const categoryIndex = this.selectedCategories.indexOf(category);
        if (categoryIndex === -1) {
          this.selectedCategories.push(category);
        } else {
          this.selectedCategories.splice(categoryIndex, 1);
        }
      }
    }

    selectNone = false;

    toggleSelectNone() {
      this.selectNone = !this.selectNone;
      if (this.selectNone) {
        this.selectedOcurrences = [];
      }
    }

    chooseOccurrence(occurenceId) {
      if (!occurenceId) {
        this.selectedOcurrences = [];
      } else {
        if (occurenceId === -1) {
          this.selectedOcurrences = [];
          this.availableSpecialOccurrences.forEach((_) => this.chooseOccurrence(_.id));
        } else {
          const occIndex = this.selectedOcurrences.indexOf(occurenceId);
          if (occIndex === -1) {
            this.selectedOcurrences.push(occurenceId);
          } else {
            this.selectedOcurrences.splice(occIndex, 1);
          }
        }
      }
    }

    addTask() {
      this.$router.push({ name: 'BoardItemDetail', params: { itemid: 'new' } });
    }

    private addingTaskFromTemplate = false;

    async addTaskFromTemplate(templateId: string) {
      this.addingTaskFromTemplate = true;
      const boardItem = await this.boardStore.addBoardItemFromTemplate(templateId);
      this.$router.push({ name: 'BoardItemDetail', params: { itemid: boardItem.id } });
      this.addingTaskFromTemplate = false;
    }

    async showModal(name: string) {
      const modal: any = this.$refs[name];
      modal.show();
    }

    private filterBySelectedLabels(source) {
      if (this.selectedLabels?.length) {
        return source.filter(
          (item) => (item as BISeparator).isFix || item.labels.some((label) => this.selectedLabels.some((_) => _.id === label.id))
        );
      }
      return source;
    }

    private filterBySelectedOcurrences(source) {
      if (this.selectedOcurrences?.length) {
        return source.filter((item) => (item as BISeparator).isFix || this.selectedOcurrences.indexOf(item.specialOccurrenceId) !== -1);
      }

      if (this.selectNone) {
        return source.filter((item) => (item as BISeparator).isFix || item.specialOccurrenceId === null);
      }
      return source;
    }

    private filterBySelectedCategories(source) {
      if (this.selectedCategories?.length) {
        return source.filter((item) => (item as BISeparator).isFix || this.selectedCategories.indexOf(item.boardItemCategory?.id) !== -1);
      } else if (this.displayIsRelevantTasksOnly) {
        return source.filter((item) => (item as BISeparator).isFix || item.isRelevantForReport);
      }
      return source;
    }

    private filterBySelectedUser(source) {
      if (this.selectedUser?.id) {
        return source.filter((item) => (item as BISeparator).isFix || item.assignedToUser?.id === this.selectedUser.id);
      }
      return source;
    }

    private filterByAllSelectedFilters(source) {
      source = this.filterBySelectedCategories(source);
      source = this.filterBySelectedOcurrences(source);
      source = this.filterBySelectedLabels(source);
      source = this.filterBySelectedUser(source);
      return source;
    }

    filterBoardItemsWithSearchInput(boardItems) {
      if (this.searchString) {
        boardItems = boardItems.filter((item) => {
          const isFix = (item as BISeparator).isFix;
          const byTitle = item.title ? item.title.toLowerCase().indexOf(this.searchString.toLowerCase()) !== -1 : false;
          const byDescription = item.description ? item.description.toLowerCase().indexOf(this.searchString.toLowerCase()) !== -1 : false;

          return isFix || byTitle || byDescription;
        });
      }

      return this.filterByAllSelectedFilters(boardItems);
    }

    get filteredItems(): IBoardItemDto[] {
      const boardItems = this.boardStore.items;

      return this.filterBoardItemsWithSearchInput(boardItems);
    }

    private filteredBoardItems(list: BoardItemListDto) {
      if (list.listGroupType === ListGroupTypeDto.Delegated) {
        let filteredDelegatedItems = [];

        if (this.isFiltersActivated) {
          this.boardStore.groupedDelegatedItems.forEach((delegatedData) => {
            const filteredData = this.filterByAllSelectedFilters(delegatedData.groupedItems);

            if (filteredData?.length) {
              const processedDelegatedData: any = {
                key: delegatedData.key,
                isGrouped: delegatedData.isGrouped,
                groupedItems: filteredData,
              };

              filteredDelegatedItems.push(processedDelegatedData);
            }
          });
        } else {
          filteredDelegatedItems = this.boardStore.groupedDelegatedItems;
        }

        return filteredDelegatedItems;
      }

      if (list.listGroupType === ListGroupTypeDto.Shared) {
        const filteredSharedItems = this.filterBoardItemsWithSearchInput(this.boardStore.sharedBoardItems);

        return this.isFiltersActivated ? this.filterByAllSelectedFilters(filteredSharedItems) : filteredSharedItems;
      }

      const listItems = this.filteredItems.filter((x) => x.boardItemListId === list.id);

      return listItems;
    }

    get isFiltersActivated() {
      return (
        this.selectedLabels?.length ||
        this.selectedCategories?.length ||
        this.selectedOcurrences?.length ||
        this.selectedUser ||
        this.displayIsRelevantTasksOnly
      );
    }

    confirmCleanup() {
      this.$bvModal
        .msgBoxConfirm(
          'ATTENTION! Cleanup will archive all currently active tasks and information items. After cleanup the board will appear empty. Are you sure you want to start the cleanup process?',
          {
            title: 'Confirm cleanup',
            size: 'sm',
            buttonSize: 'sm',
            okVariant: 'danger',
            okTitle: 'Cleanup board',
            cancelTitle: 'No',
            footerClass: 'p-2',
            hideHeaderClose: false,
            centered: false,
          }
        )
        .then((value) => {
          if (value) {
            this.boardStore.cleanupBoard(this.$route.params.id);
          }
        })
        .catch(() => {
          // An error occurred
        });
    }

    confirmArchiveDone() {
      this.$bvModal
        .msgBoxConfirm('This will archive all items in the "Done" list. Proceed?', {
          title: 'Confirm archive',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Archive done items',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: false,
        })
        .then((value) => {
          if (value) {
            this.boardStore.archiveDone(this.$route.params.id);
          }
        })
        .catch(() => {
          // An error occurred
        });
    }

    async confirmShiftPreparation() {
      await this.settingsStore.loadShiftValidationSettings(this.$route.params.id);
      let body = '';

      const validationTypes = this.getValidationTypes();

      if (validationTypes?.length) {
        const categories = this.boardStore.groupedSelect;

        validationTypes.forEach((t) => {
          const cat = categories.find((_) => _.items.some((item) => item.id === t));
          const type = cat.items.find((_) => _.id === t);

          body += `<div>- ${cat.text} - ${type.text}</div>`;
        });
      }

      return this.showPrepareShiftHandoverConfirm(body);
    }

    private getValidationTypes() {
      const validationTypes = this.settingsStore.shiftValidationSettings.types.filter((_) => _.isRelevant).map((_) => _.typeId);

      const uniqueSelectedTypes = [...new Set(this.boardStore.boardItems.map((_) => _.boardItemTypeId))];

      uniqueSelectedTypes.forEach((_) => {
        const selectedTypeIndex = validationTypes.indexOf(_);
        if (selectedTypeIndex !== -1) {
          validationTypes.splice(selectedTypeIndex, 1);
        }
      });

      return validationTypes;
    }

    private showPrepareShiftHandoverConfirm(body) {
      const h = this.$createElement;
      const titleVNode = h('div', { class: ['prepare-title'] }, 'Prepare for shift handover');

      const p1 = body
        ? 'If you prepare the board for the shift handover, reminder tasks for the following task types will be created:'
        : `All relevant task types for this board have been used during this shift. Prepare for handover will not create extra tasks.`;

      const messageVNode = h('div', { class: [''] }, [
        h('p', { class: [''] }, p1),
        h('br'),
        h('div', { domProps: { innerHTML: body } }),
        h('br'),
        h('br'),
        h('p', { class: ['prepare-notice'] }, `Relevant Task Types can be configured in the boarding settings`),
      ]);

      this.$bvModal
        .msgBoxConfirm([messageVNode], {
          title: titleVNode,
          buttonSize: 'sm',
          centered: true,
          headerClass: 'border-bottom-0',

          okVariant: 'success',
          okTitle: 'Prepare shift handover',
          hideHeaderClose: false,
          cancelTitle: 'Cancel',
        })
        .then((value) => {
          if (value) {
            this.shiftStore.prepareShiftHandover(this.$route.params.id);
          }
        });
    }

    private removeLabel(label: LabelDto) {
      const index = this.selectedLabels.findIndex((_) => _.id === label.id);

      if (index >= 0) {
        this.selectedLabels.splice(index, 1);
      }
    }

    private isConfidentialMove(boardItem: BoardItemDto, list: BoardItemListDto) {
      return (
        boardItem?.isConfidential && (list.listGroupType === ListGroupTypeDto.Shared || list.listGroupType === ListGroupTypeDto.Delegated)
      );
    }
  }
</script>

<style lang="scss">
  .delegate-modal-content {
    padding-right: 1.5em !important;
  }

  .boardTitle {
    color: #333;
    font-size: 1.3rem;
  }

  .all-info {
    float: left;

    :hover {
      cursor: pointer;
    }
  }

  .fade-enter-active,
  .fade-leave-active {
    transition: opacity 0.1s ease-out;
  }

  .fade-enter,
  .fade-leave-to {
    opacity: 0;
  }

  .c-container {
    border-radius: 4px;

    font-weight: bold;
    text-align: center;
  }

  .currentShift {
    color: #333;
    font-size: 0.8rem;
    white-space: nowrap;
    margin-right: 10px;

    div:first-child {
      font-weight: bold;
    }
  }

  .bahn {
    margin: 10px;

    .headers {
      font-size: 1.1rem;
      padding: 4px 0;

      font-weight: bold;
      text-align: center;
      margin-left: 8px;
      margin-right: 8px;
      border-radius: 4px 4px 0 0;
      background: darken(#eaeaea, 8%);
    }

    .arrow {
      float: right;

      :hover {
        cursor: pointer;
      }
    }

    .containers {
      padding: 5px 5px 30px;
      box-shadow: 0 2px 2px rgba(0, 0, 0, 0.12);
      width: 350px;
      min-height: 50px;
      background: #eaeaea;
      border-radius: 4px;
      max-height: calc(90vh - 50px);
      overflow-y: auto;
      overflow-x: auto;
    }

    .containers::-webkit-scrollbar {
      width: 8px;
    }

    .containers::-webkit-scrollbar-track {
      background: #ddd;
    }

    .containers::-webkit-scrollbar-thumb {
      background: #999;
      border: 2px solid #ddd;
    }

    &.information {
      .containers {
        background: lighten(#357ebd, 40%);
      }

      .headers {
        background: #357ebd;
        color: white;
      }
    }
  }

  .board-card {
    padding: 3px;
    margin: 5px;
    border: none;
    border-bottom: solid 2px #ccc;
    box-shadow: none;
    font-size: 1.1rem;
    background: lighten(#c4c4c4, 0%);
    color: white;
    border-radius: 6px 6px 0 0;
  }

  .timecard {
    padding: 6px;
    background: #eaeaea;
    border: none;
    border-bottom: solid 2px #ccc;
    box-shadow: none;
    font-size: 0.8rem;
    background: lighten(#357ebd, 0%);
    color: white;
    border-radius: 6px 6px 0 0;
    cursor: pointer;

    &.nextshift {
      background: orange;
      color: black;
      font-weight: bold;
    }
  }

  .card-ghost {
  }

  .card-ghost-drop {
  }

  .prepare-title {
    font-weight: bold;
  }

  .prepare-notice {
    font-size: 10px;
  }
</style>
